import { resolveQuestionnaireLanguageDisplayLabel } from 'src/components/pages/CoiFormManagement/questionnaireListDisplay';
import {
  DataGridColumnType,
  FormManagementQuestionnaireByIdQuery,
  FormManagementQuestionsQuery,
} from 'src/graphql';
import { LanguageOption } from 'src/utils/languages';
import {
  mapApiOptionTranslations,
  mapApiQuestionTranslations,
} from './mappers/mapApiQuestionTranslations';
import { API_TYPE_TO_UI_TYPE, QuestionData, QuestionOption } from './types';

type ApiQuestion = NonNullable<
  FormManagementQuestionsQuery['formManagementQuestions']['questions']
>[number];

type ApiQuestionnaire =
  FormManagementQuestionnaireByIdQuery['formManagementQuestionnaireById'];

// Extended type for branching logic fields
type ApiQuestionOptionWithBranching = NonNullable<
  ApiQuestion['options']
>[number] & {
  navigationAction?: string | null;
  targetQuestionId?: string | null;
  shouldAutoFlag?: boolean | null;
};

function mapApiOptionToQuestionOption(
  opt: NonNullable<ApiQuestion['options']>[number],
  questionType?: string,
  index?: number,
): QuestionOption {
  const optionText = opt.optionText ?? '';
  const supportsOtherOption =
    questionType === 'radio' || questionType === 'checkbox';
  const isOtherOption =
    supportsOtherOption && optionText === 'Other (optional)';

  const optionTranslations = mapApiOptionTranslations(opt.translations);

  // Transform backend navigationAction to frontend nextAction
  const optWithBranching = opt as ApiQuestionOptionWithBranching;
  let nextAction: string | undefined;
  const navAction = optWithBranching.navigationAction;
  const targetQId = optWithBranching.targetQuestionId;

  if (navAction === 'next_question') {
    nextAction = 'next';
  } else if (navAction === 'submit_form') {
    nextAction = 'submit';
  } else if (navAction === 'jump_to_question') {
    if (targetQId) {
      nextAction = `question:${targetQId}`;
    } else {
      console.error(
        '[mapApiOptionToQuestionOption] jump_to_question missing targetQuestionId - data corruption',
        { optionId: opt.id },
      );
      nextAction = 'next';
    }
  }

  return {
    id: opt.id ?? `option-${opt.optionOrder ?? index ?? 0}`,
    text: optionText,
    ...(nextAction && { nextAction }),
    ...(typeof optWithBranching.shouldAutoFlag === 'boolean' && {
      isFlagged: optWithBranching.shouldAutoFlag,
    }),
    ...(optionTranslations?.length && { translations: optionTranslations }),
    ...(isOtherOption && {
      isOtherOption: true,
      helperText:
        'When the user selects "Other," a text field appears where they can enter their own answer.',
    }),
  };
}

function mapDataGridColumn(col: DataGridColumnType) {
  return {
    key: col.key ?? '',
    label: col.label ?? '',
    type: col.type ?? 'text',
    required: col.required ?? false,
    ...(col.maxLength != null && { maxLength: col.maxLength }),
    ...(col.options && { options: col.options }),
    ...(col.min != null && { min: col.min }),
    ...(col.max != null && { max: col.max }),
  };
}

export function mapApiQuestionToQuestionData(
  apiQuestion: ApiQuestion,
  index: number,
): QuestionData {
  const typeKey = apiQuestion.questionType?.typeKey ?? '';
  const uiType = API_TYPE_TO_UI_TYPE[typeKey] ?? typeKey;

  const options = apiQuestion.options
    ?.slice()
    .sort((a, b) => (a.optionOrder ?? 0) - (b.optionOrder ?? 0))
    .map((opt, idx) => mapApiOptionToQuestionOption(opt, uiType, idx));

  const questionTranslations = mapApiQuestionTranslations(
    apiQuestion.translations,
  );

  const questionData: QuestionData = {
    id: apiQuestion.id ?? `question-${apiQuestion.orderIndex ?? index}`,
    questionText: apiQuestion.questionText ?? '',
    description: apiQuestion.description ?? '',
    questionType: uiType || undefined,
    isRequired: apiQuestion.isRequired ?? false,
    order: apiQuestion.orderIndex ?? index,
    displayNumber: index + 1,
    options: options && options.length > 0 ? options : undefined,
    ...(questionTranslations?.length && { translations: questionTranslations }),
    ...(typeof apiQuestion.shouldAutoFlagIfAnswered === 'boolean' && {
      flagIfAnswered: apiQuestion.shouldAutoFlagIfAnswered,
    }),
  };

  if (uiType === 'date_picker' || uiType === 'time_picker') {
    questionData.dateTimeOptions = {
      datePicker: uiType === 'date_picker',
      timePicker: uiType === 'time_picker',
    };
  }

  if (uiType === 'database_field' && apiQuestion.databaseFieldSubTypeId) {
    questionData.databaseFieldOptions = {
      selectedFieldId: apiQuestion.databaseFieldSubTypeId,
      allowMultiSelect: apiQuestion.allowMultiSelect ?? false,
    };
  }

  // Map dataGridConfig if present
  if (apiQuestion.dataGridConfig) {
    const { columns, minRows, maxRows, addRowLabel, removeRowLabel } =
      apiQuestion.dataGridConfig;
    questionData.dataGridConfig = {
      columns: columns?.map(mapDataGridColumn) ?? [],
      ...(minRows != null && { minRows }),
      ...(maxRows != null && { maxRows }),
      ...(addRowLabel && { addRowLabel }),
      ...(removeRowLabel && { removeRowLabel }),
    };

    // Convert dataGridConfig columns to options for UI display
    if (columns && columns.length > 0) {
      questionData.options = columns.map((col, idx) => ({
        id: col.key ?? `col-${idx}`,
        text: col.label ?? '',
        nextAction: col.type,
        // Map column translations to option translations format (filter out invalid entries)
        translations: col.translations
          ?.filter((t) => t?.languageCode && t?.label)
          .map((t) => ({
            languageCode: t.languageCode!,
            optionText: t.label!,
          })),
      }));
    }
  }

  return questionData;
}

export function mapApiQuestionsToFormState(
  questions: ApiQuestion[],
): QuestionData[] {
  return questions
    .slice()
    .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
    .map((q, i) => mapApiQuestionToQuestionData(q, i));
}

export function mapApiQuestionnaireToFormState(
  questionnaire: ApiQuestionnaire,
) {
  const languages: LanguageOption[] =
    questionnaire.languages
      ?.filter((l) => l.code?.trim())
      .sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0))
      .map((l) => ({
        label: resolveQuestionnaireLanguageDisplayLabel(l.code, l.name),
        value: l.code!.trim(),
        isDefault: l.isDefault ?? undefined,
      })) ?? [];

  // Map questionnaire translations to titleTranslations and descriptionTranslations
  // Exclude the default locale (en-GB) as it's stored in the base title/description fields
  const titleTranslations: Record<string, string> = {};
  const descriptionTranslations: Record<string, string> = {};

  questionnaire.questionnaireTranslations?.forEach((t) => {
    if (t.languageCode && t.languageCode !== 'en-GB') {
      if (t.publicTitle) {
        titleTranslations[t.languageCode] = t.publicTitle;
      }
      if (t.description) {
        descriptionTranslations[t.languageCode] = t.description;
      }
    }
  });

  return {
    title: questionnaire.publicTitle ?? questionnaire.internalName ?? '',
    description: questionnaire.introduction ?? questionnaire.description ?? '',
    categoryName: questionnaire.category ?? '',
    categoryId: questionnaire.categoryId ?? null,
    languages,
    titleTranslations,
    descriptionTranslations,
    autoReviewEnabled: questionnaire.autoReviewEnabled,
    countries: questionnaire.countries ?? [],
  };
}
